import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { FilePlus2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';
import { Input } from '@ooh/ui/components/input';
import { SaveCampaignDialog } from '@ooh/ui/components/save-campaign-dialog';
import type { Attachment } from '@ooh/contracts';
import { ATTACHMENT_INPUT_ACCEPT } from './lib/attachment-constants';
import { useDeleteAttachment } from './api/mutations';
import {
  AttachmentList,
  UploadZoneContent,
  useUploadState,
  type UploadZoneLabels,
} from './tech-spec-upload';

interface TechSpecCardProps {
  mediaUnitId: string | number;
  attachments: Attachment[];
  /** When false, upload zone and delete actions are hidden. Default: true */
  canManage?: boolean;
}

export function TechSpecCard({ mediaUnitId, attachments, canManage = true }: TechSpecCardProps) {
  const { t } = useTranslation('inventory');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [pendingDeleteId, setPendingDeleteId] = React.useState<number | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const deleteMutation = useDeleteAttachment();

  const {
    isUploading,
    showSizeError,
    showTypeError,
    showServerError,
    serverErrorMessage,
    handleFile,
  } = useUploadState(mediaUnitId, t('detail.techSpec.errorType'));

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }

  const handleDrop = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    // Drag-and-drop is intentionally not supported per design decision
    // Use the file picker button instead
  }, []);

  const uploadZoneBorderClass = isDragOver
    ? 'border-primary bg-primary/5'
    : showSizeError || showTypeError || showServerError
      ? 'border-destructive/60 bg-destructive/5'
      : 'border-border/60 bg-muted/20 hover:border-border hover:bg-muted/30';

  const uploadZoneLabels: UploadZoneLabels = {
    uploading: t('detail.techSpec.uploading'),
    errorSize: t('detail.techSpec.errorSize'),
    errorSizeHint: t('detail.techSpec.errorSizeHint'),
    errorType: t('detail.techSpec.errorType'),
    errorTypeHint: t('detail.techSpec.errorTypeHint'),
    dragOver: t('detail.techSpec.dragOver'),
    uploadZoneHint: t('detail.techSpec.uploadZoneHint'),
    uploadZoneFormats: t('detail.techSpec.uploadZoneFormats'),
    uploadButton: t('detail.techSpec.uploadButton'),
  };

  return (
    <Card data-testid="tech-spec-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-[15px] flex items-center gap-2">
          <FilePlus2 className="h-4 w-4 text-muted-foreground" />
          {t('detail.techSpec.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {canManage ? (
          <div
            data-testid="upload-zone"
            className={`relative rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${uploadZoneBorderClass}`}
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
          >
            <UploadZoneContent
              isDragOver={isDragOver}
              isUploading={isUploading}
              showSizeError={showSizeError}
              showTypeError={showTypeError}
              showServerError={showServerError}
              serverErrorMessage={serverErrorMessage}
              onButtonClick={() => fileInputRef.current?.click()}
              labels={uploadZoneLabels}
            />
            <Input
              ref={fileInputRef}
              type="file"
              accept={ATTACHMENT_INPUT_ACCEPT}
              className="sr-only"
              data-testid="attachment-file-input"
              onChange={handleInputChange}
              aria-label={t('detail.techSpec.uploadButton')}
            />
          </div>
        ) : null}

        <AttachmentList
          attachments={attachments}
          isUploading={isUploading}
          emptyLabel={t('detail.techSpec.emptyState')}
          onDelete={canManage ? (id) => setPendingDeleteId(id) : undefined}
        />
      </CardContent>

      {pendingDeleteId !== null && (
        <SaveCampaignDialog
          title={t('detail.techSpec.deleteConfirmTitle')}
          description={
            deleteError !== null
              ? t('detail.techSpec.deleteError')
              : t('detail.techSpec.deleteConfirmDescription')
          }
          cancelLabel={t('detail.techSpec.deleteConfirmCancel')}
          saveLabel={t('detail.techSpec.deleteConfirmConfirm')}
          onClose={() => {
            setPendingDeleteId(null);
            setDeleteError(null);
          }}
          onSave={() => {
            setDeleteError(null);
            deleteMutation.mutate(
              { mediaUnitId, attachmentId: pendingDeleteId },
              {
                onSuccess: () => {
                  setPendingDeleteId(null);
                  setDeleteError(null);
                },
                onError: () => {
                  setDeleteError(t('detail.techSpec.deleteError'));
                },
              },
            );
          }}
        />
      )}
    </Card>
  );
}
